import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { CURRENCY_DECIMALS, DEFAULT_CURRENCY_DECIMALS, convertMoney, formatMoney } from '@theme/money-formatting';

/**
 * Shows the "Pay in 4" message once the line total clears the minimum, and keeps the payment
 * amount in step with the quantity and the selected variant.
 *
 * The minimum is configured in the shop's currency while the price is already in the market's, so
 * the two can only be compared after `Shopify.currency.rate` is applied. Liquid has no exchange
 * rate, which is why the decision cannot stay on the server.
 *
 * @property {string} data-unit-price - Selected variant's price in major units, from `money_amount`
 * @property {string} data-min-price - Minimum line total, in the shop's currency, major units
 * @property {string} data-money-format - `shop.money_format`; only its amount placeholder is used
 *
 * @typedef {object} ProductInstallmentsRefs
 * @property {HTMLElement} [amount] - Wraps the payment amount inside the merchant's message
 *
 * @extends {Component<ProductInstallmentsRefs>}
 */
class ProductInstallments extends Component {
  /** Afterpay splits every order four ways. */
  static payments = 4;

  #abortController = new AbortController();

  /** Selected variant's price, major units, market currency. @type {number} */
  #unitPrice = 0;

  /** Selected variant, so an Intelligems test price can stand in for `#unitPrice`. @type {string} */
  #variantId = '';

  connectedCallback() {
    super.connectedCallback();

    this.#unitPrice = Number(this.dataset.unitPrice) || 0;
    this.#variantId = this.dataset.variantId ?? '';

    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    this.#render();
    this.#renderOnceIntelligemsLoads();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Quantity only ever changes on the one-time-purchase option — `blocks/purchase-options.liquid`
   * hides the stepper for a subscription — so no branch on the checked option is needed.
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    const { cartLine } = /** @type {{ cartLine?: number }} */ (/** @type {any} */ (event).detail ?? {});

    // A cart line's stepper, not this product's.
    if (cartLine) return;
    if (!this.#contains(event.target)) return;

    this.#render();
  };

  /** @param {Event} event */
  #handleVariantUpdate = (event) => {
    if (!this.#contains(event.target)) return;

    const price = /** @type {any} */ (event).detail?.resource?.price;
    const variantId = /** @type {any} */ (event).detail?.resource?.id;
    if (variantId) this.#variantId = String(variantId);

    // Liquid renders every price as hundredths whatever the currency, so this is major units.
    if (typeof price === 'number') this.#unitPrice = price / 100;

    this.#render();
  };

  /**
   * The block sits beside the buy buttons rather than inside the form, so events are matched
   * against the whole section instead of `closest('product-form-component')`.
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  #contains(target) {
    const scope = this.closest('.shopify-section, dialog');
    return Boolean(scope && target instanceof Node && scope.contains(target));
  }

  /** @returns {number} */
  #quantity() {
    const scope = this.closest('.shopify-section, dialog');
    const input = /** @type {HTMLInputElement | null} */ (
      scope?.querySelector('product-form-component input[name="quantity"]') ?? null
    );
    const quantity = parseInt(input?.value ?? '', 10);

    return quantity > 0 ? quantity : 1;
  }

  /**
   * Intelligems loads after this component, so re-render once its price API is up.
   */
  #renderOnceIntelligemsLoads() {
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (/** @type {any} */ (window).igData?.price?.getPriceByVariantId) {
        clearInterval(timer);
        this.#render();
      } else if (tries > 50) {
        clearInterval(timer);
      }
    }, 200);
    this.#abortController.signal.addEventListener('abort', () => clearInterval(timer));
  }

  /**
   * The variant's price under an active Intelligems test, else the theme's own.
   * @returns {number}
   */
  #effectiveUnitPrice() {
    const getPrice = /** @type {any} */ (window).igData?.price?.getPriceByVariantId;
    // IG prices are in the shop's currency; another market keeps the theme's converted price.
    const inShopCurrency = Number(window.Shopify?.currency?.rate ?? 1) === 1;
    if (this.#variantId && inShopCurrency && typeof getPrice === 'function') {
      const testPrice = parseFloat(getPrice(this.#variantId));
      if (testPrice > 0) return testPrice;
    }
    return this.#unitPrice;
  }

  #render() {
    const total = this.#effectiveUnitPrice() * this.#quantity();
    const minimum = convertMoney(Number(this.dataset.minPrice) || 0);

    // Left as Liquid rendered it in the editor, so a merchant can still select and configure it.
    if (!window.Shopify?.designMode) this.hidden = total <= minimum;

    const { amount } = this.refs;
    const currency = window.Shopify?.currency?.active;
    if (!amount || !currency) return;

    // Whole currency units before dividing, matching the headless storefront's `parseInt`.
    const installment = Math.floor(total) / ProductInstallments.payments;

    // The amount shows without a currency symbol, so only the shop format's placeholder is kept.
    const format = this.dataset.moneyFormat?.match(/{{\s*\w+\s*}}/)?.[0] ?? '{{amount}}';
    const precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

    amount.textContent = formatMoney(Math.round(installment * Math.pow(10, precision)), format, currency);
  }
}

if (!customElements.get('product-installments')) {
  customElements.define('product-installments', ProductInstallments);
}
